import type { KeyboardEvent, ReactNode } from 'react';
import { cn } from '@eombor/ui';

export interface ChoiceOption<Value extends string> {
  value: Value;
  label: string;
  /** Yorliq yonidagi kichik belgi — bayroq yoki tema ikonkasi. */
  icon?: ReactNode;
}

export interface ChoiceGroupProps<Value extends string> {
  /** Guruhning nomi — ekran o'qigich uchun ("Til", "Ko'rinish"). */
  label: string;
  value: Value;
  options: readonly ChoiceOption<Value>[];
  onChange: (value: Value) => void;
  className?: string;
}

/**
 * BITTA SOZLAMANING qiymatini tanlash — radio guruh.
 *
 * `Segmented` dan farqi semantikada, ko'rinishda emas. U ko'rinishlar
 * orasidagi NAVIGATSIYA (bosilganda manzil o'zgaradi va sahifa qayta
 * chiziladi) va shuning uchun `aria-pressed` li tugmalar guruhi. Bu esa
 * saqlanadigan qiymatni tanlaydi: ekran o'qigich "uchtadan biri"
 * ekanini va qaysi biri tanlanganini aytishi kerak.
 *
 * Klaviatura radio guruh qoidasi bo'yicha: `Tab` guruhga BITTA to'xtash
 * beradi, ichida esa o'q tugmalari yuradi va darhol tanlaydi. Aks holda
 * ikkita sozlama olti marta `Tab` bosishni talab qilardi.
 */
export const ChoiceGroup = <Value extends string>({
  label,
  value,
  options,
  onChange,
  className,
}: ChoiceGroupProps<Value>) => {
  const index = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );

  const move = (delta: number): void => {
    const next = options[(index + delta + options.length) % options.length];
    if (next !== undefined) onChange(next.value);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      move(1);
      return;
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      move(-1);
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={label}
      onKeyDown={onKeyDown}
      className={cn(
        'relative flex h-[var(--size-control)] items-stretch',
        'rounded-[var(--radius-sm)] border border-border bg-surface-2 p-1',
        className,
      )}
    >
      {/*
        SILJIYDIGAN indikator.

        Har bir tugmaga alohida fon berilsa, tanlov bir joydan
        ikkinchisiga "sakrab" o'tardi va ko'z qayerdan qayerga
        ketganini ilg'amasdi. Bitta indikator esa yo'lni ko'rsatadi.
        `transform` — kompozitor qatlamida ishlaydi va sahifani qayta
        hisoblashga majburlamaydi; `prefers-reduced-motion` da
        davomiylik global qoida bilan nolga tushadi (`tokens.css`).
      */}
      <span
        aria-hidden
        className={cn(
          'absolute top-1 bottom-1 left-1 rounded-[var(--radius-sm)]',
          'bg-surface shadow-[var(--shadow-1)]',
          'transition-transform duration-[var(--motion-base)] ease-[var(--ease-standard)]',
        )}
        style={{
          width: `calc((100% - 0.5rem) / ${String(options.length)})`,
          transform: `translateX(${String(index * 100)}%)`,
        }}
      />

      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            // Guruh ichida FAQAT tanlangani `Tab` bilan topiladi —
            // qolganlariga o'q tugmalari olib boradi.
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(option.value)}
            className={cn(
              'relative z-10 flex flex-1 items-center justify-center gap-1.5 px-2',
              'rounded-[var(--radius-sm)] text-sm font-medium whitespace-nowrap',
              'transition-colors duration-[var(--motion-fast)] ease-[var(--ease-standard)]',
              'active:scale-[0.96] active:transition-transform',
              active ? 'text-text' : 'text-text-muted hover:text-text',
            )}
          >
            {option.icon}
            {option.label}
          </button>
        );
      })}
    </div>
  );
};
